import React, { useState } from 'react';
import {
  AlertTriangle,
  Lock,
  Globe,
  Clock,
  ShieldX,
  Upload,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Info,
  X
} from 'lucide-react';

/**
 * YouTube Error Types and their configurations
 * Maps backend error patterns to user-friendly displays
 */
const YOUTUBE_ERROR_TYPES = {
  // Permission/Access errors
  ACCESS_RESTRICTED: {
    pattern: /video access restricted|private|unlisted|not owned by the user|permissiondenied|403/i,
    icon: Lock,
    title: 'Video Access Restricted',
    color: 'orange',
    description: 'This video is private, unlisted, or has restricted access permissions.',
    suggestions: [
      'Ensure the video is set to "Public" on YouTube',
      'If it\'s your video, check the visibility settings in YouTube Studio',
      'Try uploading the video file directly instead of using the YouTube URL'
    ]
  },
  // Region-locked content
  REGION_LOCKED: {
    pattern: /region|geo.?block|country|not available in your|location/i,
    icon: Globe,
    title: 'Region-Locked Content',
    color: 'blue',
    description: 'This video is not available in your region due to geographic restrictions.',
    suggestions: [
      'The video creator has restricted access to certain countries',
      'Download the video if you have access and upload it directly',
      'Contact the video owner for an alternative link'
    ]
  },
  // Copyright/Usage rights
  COPYRIGHT: {
    pattern: /copyright|usage rights|claim|blocked|dmca|content id/i,
    icon: ShieldX,
    title: 'Copyright or Usage Rights Issue',
    color: 'red',
    description: 'This video has copyright restrictions that prevent automated processing.',
    suggestions: [
      'The video contains copyrighted content that restricts external access',
      'Try using a video without copyrighted music or content',
      'Upload your own recording of the footage directly'
    ]
  },
  // Video too long
  TOO_LONG: {
    pattern: /too long|duration|maximum length|over.*hour|exceeds.*limit|above 3hr/i,
    icon: Clock,
    title: 'Video Too Long',
    color: 'purple',
    description: 'The video exceeds the maximum duration limit for analysis.',
    suggestions: [
      'Videos over 1 hour may fail to process',
      'Try using a shorter clip (under 10 minutes recommended)',
      'Split the video into smaller segments and analyze separately',
      'Upload the video file directly for better processing of longer content'
    ]
  },
  // Format issues
  FORMAT_ISSUE: {
    pattern: /format|quality|invalid_argument|invalidargument|not supported|400/i,
    icon: AlertTriangle,
    title: 'Video Format Issue',
    color: 'yellow',
    description: 'The video format or quality may not be compatible with our analysis system.',
    suggestions: [
      'The video may have an unsupported format or quality setting',
      'Try a different video with standard resolution (720p-1080p)',
      'Download and convert the video to MP4 format, then upload directly'
    ]
  },
  // Video not found
  NOT_FOUND: {
    pattern: /not found|404|does not exist|removed|deleted|unavailable/i,
    icon: AlertTriangle,
    title: 'Video Not Found',
    color: 'gray',
    description: 'The video could not be found. It may have been removed or the URL is incorrect.',
    suggestions: [
      'Double-check the YouTube URL is correct',
      'The video may have been deleted by the owner',
      'Try searching for the video on YouTube to verify it exists'
    ]
  },
  // Network/timeout errors
  NETWORK_ERROR: {
    pattern: /timeout|network|connection|timed out|service.*unavailable/i,
    icon: AlertTriangle,
    title: 'Connection Issue',
    color: 'gray',
    description: 'There was a problem accessing the video due to network issues.',
    suggestions: [
      'Check your internet connection and try again',
      'The service may be temporarily unavailable',
      'Try again in a few minutes'
    ]
  },
  // Quota/rate limit
  QUOTA_EXCEEDED: {
    pattern: /quota|rate limit|too many requests/i,
    icon: Clock,
    title: 'Service Temporarily Unavailable',
    color: 'orange',
    description: 'Our video analysis service is experiencing high demand.',
    suggestions: [
      'Please wait a few minutes before trying again',
      'Try uploading the video file directly instead'
    ]
  },
  // Default/unknown error
  UNKNOWN: {
    pattern: /.*/,
    icon: AlertTriangle,
    title: 'Analysis Failed',
    color: 'red',
    description: 'We encountered an issue while analyzing this YouTube video.',
    suggestions: [
      'Try uploading the video file directly instead of using the YouTube URL',
      'Ensure the video is publicly accessible',
      'Try again with a different video'
    ]
  }
};

/**
 * Determine the error type from the error message
 */
const getErrorType = (errorMessage) => {
  if (!errorMessage) return YOUTUBE_ERROR_TYPES.UNKNOWN;

  for (const [key, config] of Object.entries(YOUTUBE_ERROR_TYPES)) {
    if (key === 'UNKNOWN') continue; // Check UNKNOWN last
    if (config.pattern.test(errorMessage)) {
      return config;
    }
  }
  return YOUTUBE_ERROR_TYPES.UNKNOWN;
};

/**
 * Get color classes based on error type — OLED dark only
 */
const getColorClasses = (color) => {
  const colors = {
    red: {
      bg: 'bg-red-900/30',
      border: 'border-red-700',
      text: 'text-red-300',
      icon: 'text-red-400'
    },
    orange: {
      bg: 'bg-orange-900/30',
      border: 'border-orange-700',
      text: 'text-orange-300',
      icon: 'text-orange-400'
    },
    yellow: {
      bg: 'bg-[#E8B800]/10',
      border: 'border-[#E8B800]/40',
      text: 'text-[#E8B800]',
      icon: 'text-[#E8B800]'
    },
    blue: {
      bg: 'bg-blue-900/30',
      border: 'border-blue-700',
      text: 'text-blue-300',
      icon: 'text-blue-400'
    },
    purple: {
      bg: 'bg-purple-900/30',
      border: 'border-purple-700',
      text: 'text-purple-300',
      icon: 'text-purple-400'
    },
    gray: {
      bg: 'bg-[#111111]',
      border: 'border-[#1A1A1A]',
      text: 'text-[#999999]',
      icon: 'text-[#555555]'
    }
  };
  return colors[color] || colors.gray;
};

/**
 * YouTubeErrorDisplay - Displays detailed YouTube failure reasons
 * with actionable suggestions for users
 */
const YouTubeErrorDisplay = ({
  errorMessage,
  onDismiss,
  onUploadClick,
  showUploadButton = true,
  compact = false,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(!compact);

  const errorType = getErrorType(errorMessage);
  const colorClasses = getColorClasses(errorType.color);
  const IconComponent = errorType.icon;

  if (compact) {
    return (
      <div
        className={`flex items-start gap-3 p-3 rounded-lg border ${colorClasses.bg} ${colorClasses.border} ${className}`}
        role="alert"
      >
        <IconComponent className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colorClasses.icon}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className={`font-semibold text-sm ${colorClasses.text}`}>
              {errorType.title}
            </h4>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`p-1 rounded hover:bg-[rgba(255,255,255,0.04)] transition-colors ${colorClasses.text}`}
              aria-label={isExpanded ? 'Show less' : 'Show more'}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>

          {isExpanded && (
            <div className="mt-2 space-y-2">
              <p className="text-sm text-[#999999]">
                {errorType.description}
              </p>
              {showUploadButton && onUploadClick && (
                <button
                  onClick={onUploadClick}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  <Upload size={14} />
                  Upload Video Directly
                </button>
              )}
            </div>
          )}
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`p-1 rounded hover:bg-[rgba(255,255,255,0.04)] transition-colors ${colorClasses.text}`}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-[14px] border overflow-hidden ${colorClasses.bg} ${colorClasses.border} ${className}`}
      role="alert"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-inherit">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-black/20">
            <IconComponent className={`w-6 h-6 ${colorClasses.icon}`} />
          </div>
          <div>
            <h4 className={`font-semibold ${colorClasses.text}`}>
              {errorType.title}
            </h4>
            <p className="text-sm text-[#555555]">
              YouTube Video Analysis Failed
            </p>
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`p-2 rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-colors ${colorClasses.text}`}
            aria-label="Dismiss"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Description */}
        <p className="text-[#999999]">
          {errorType.description}
        </p>

        {/* Suggestions */}
        <div>
          <h5 className="font-medium mb-2 flex items-center gap-2 text-[#F5F5F5]">
            <HelpCircle size={16} />
            What you can do:
          </h5>
          <ul className="space-y-2">
            {errorType.suggestions.map((suggestion, index) => (
              <li
                key={index}
                className="flex items-start gap-2 text-sm text-[#999999]"
              >
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${colorClasses.icon.replace('text-', 'bg-')}`} />
                {suggestion}
              </li>
            ))}
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-2">
          {showUploadButton && onUploadClick && (
            <button
              onClick={onUploadClick}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Upload size={16} />
              Upload Video Directly
            </button>
          )}
          <a
            href="https://support.google.com/youtube/answer/157177"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors border-[#1A1A1A] text-[#999999] hover:bg-[rgba(255,255,255,0.04)]"
          >
            <ExternalLink size={16} />
            YouTube Privacy Settings
          </a>
        </div>
      </div>
    </div>
  );
};

/**
 * YouTubeRestrictionsTooltip - Informational tooltip explaining common YouTube restrictions
 */
export const YouTubeRestrictionsTooltip = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);

  const restrictions = [
    {
      icon: Lock,
      title: 'Private/Unlisted Videos',
      description: 'Videos set to private or unlisted cannot be analyzed. Make sure the video is public.'
    },
    {
      icon: ShieldX,
      title: 'Copyright Restrictions',
      description: 'Videos with copyrighted content may block external access for automated processing.'
    },
    {
      icon: Globe,
      title: 'Region-Locked Content',
      description: 'Some videos are only available in certain countries and cannot be accessed globally.'
    },
    {
      icon: Clock,
      title: 'Duration Limits',
      description: 'Very long videos (over 1 hour) may fail to process. Shorter clips work best.'
    }
  ];

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors text-[#555555] hover:text-[#F5F5F5]"
        aria-label="YouTube restrictions information"
      >
        <Info size={16} />
        <span>YouTube Restrictions</span>
      </button>

      {isOpen && (
        <div
          className="absolute z-50 w-80 mt-2 p-4 rounded-[14px] shadow-xl border bg-[#111111] border-[#1A1A1A]"
          style={{ left: '50%', transform: 'translateX(-50%)' }}
        >
          <h4 className="font-semibold mb-3 text-[#F5F5F5]">
            Common YouTube Restrictions
          </h4>
          <div className="space-y-3">
            {restrictions.map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <item.icon className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#555555]" />
                <div>
                  <h5 className="font-medium text-sm text-[#F5F5F5]">
                    {item.title}
                  </h5>
                  <p className="text-xs text-[#555555]">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t text-xs border-[#1A1A1A] text-[#555555]">
            <strong>Tip:</strong> For best results, upload video files directly instead of using YouTube URLs.
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Utility function to check if an error message is a YouTube-related error
 */
export const isYouTubeError = (errorMessage) => {
  if (!errorMessage) return false;
  const youtubePatterns = [
    /youtube/i,
    /video access restricted/i,
    /private.*video/i,
    /unlisted/i,
    /region/i,
    /copyright/i,
    /too long/i,
    /duration/i,
    /permissiondenied/i,
    /not owned by the user/i
  ];
  return youtubePatterns.some(pattern => pattern.test(errorMessage));
};

/**
 * Utility to parse backend error responses
 */
export const parseYouTubeError = (errorMessage) => {
  return getErrorType(errorMessage);
};

export default YouTubeErrorDisplay;
